import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, getDocs, query, where, doc, updateDoc } from "firebase/firestore";
import { motion } from "framer-motion";
import { AlertCircle, Brain, Key, LogOut, Users, Copy } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "../components/Toast";
import { isUserCoach } from "../lib/auth";
import { auth, db } from "../lib/firebase";
import { LoadingSpinner } from "../components/LoadingSpinner";

export function CoachDashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true); // @ts-ignore
  const [error, setError] = useState(""); // @ts-ignore
  const [affiliationData, setAffiliationData] = useState({
    link: '',
    buttonText: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAffiliationToast, setShowAffiliationToast] = useState(false);
  const [affiliationToastMessage, setAffiliationToastMessage] = useState('');
  const [affiliationToastType, setAffiliationToastType] = useState<'success' | 'error'>('success');
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [stats, setStats] = useState({
    totalRespondents: 0,
    completedAssessments: 0,
    activeRespondents: 0,
  }); // @ts-ignore
  const [assessmentCode, setAssessmentCode] = useState<string>(""); // @ts-ignore
  const [hasAiAccess, setHasAiAccess] = useState(false); // @ts-ignore
  const [hasManualAiAccess, setHasManualAiAccess] = useState(false); // @ts-ignore
  const [coachDocId, setCoachDocId] = useState<string>(""); // @ts-ignore
  const [defaultAiAccess, setDefaultAiAccess] = useState(false);
  const [isUpdatingAiAccess, setIsUpdatingAiAccess] = useState(false);
  const [recentActivity, setRecentActivity] = useState<Array<{
    id: string;
    type: 'registration' | 'assessment_completed';
    respondentName: string;
    timestamp: Date;
    details?: string;
  }>>([]);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(assessmentCode);
      setShowCopyToast(true);
    } catch (error) {
      console.error('Failed to copy code:', error);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = assessmentCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopyToast(true);
    }
  };

  useEffect(() => {
    checkCoachAccess();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error("Error signing out:", err);
    }
  }, [navigate]);

  const handleAffiliationUpdate = async () => {
    try {
      setIsUpdating(true);
      
      if (!auth.currentUser?.uid) {
        throw new Error('User not authenticated');
      }

      // First get the coach document
      const coachQuery = query(
        collection(db, "coaches"),
        where("userId", "==", auth.currentUser.uid)
      );
      const coachSnapshot = await getDocs(coachQuery);
      
      if (coachSnapshot.empty) {
        throw new Error('Coach document not found');
      }
      
      // Update the coach document with affiliation data
      const coachDocRef = doc(db, 'coaches', coachSnapshot.docs[0].id);
      await updateDoc(coachDocRef, {
        affiliationLink: affiliationData.link.trim(),
        affiliationButtonText: affiliationData.buttonText.trim(),
        updatedAt: new Date()
      });
      
      showAffiliationToastFunc('Affiliation settings updated successfully!', 'success');
    } catch (err) {
      console.error('Error updating affiliation:', err);
      showAffiliationToastFunc('Failed to update affiliation settings', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const showAffiliationToastFunc = (message: string, type: 'success' | 'error') => {
    setAffiliationToastMessage(message);
    setAffiliationToastType(type);
    setShowAffiliationToast(true);
  };

  const loadCoachData = async () => {
    try {
      if (!auth.currentUser) return;

      // Get coach document to load all coach data
      const coachQuery = query(
        collection(db, "coaches"),
        where("userId", "==", auth.currentUser.uid)
      );
      const coachSnapshot = await getDocs(coachQuery);

      if (!coachSnapshot.empty) {
        const coachData = coachSnapshot.docs[0].data();
        
        // Set assessment code
        setAssessmentCode(coachData.assessmentCode || "");
        
        // Check for full AI access (advanced/partner tiers)
        setHasAiAccess(coachData.aiAnalysisAccess === true);
        // Check for manual AI access (basic plus tier)
        setHasManualAiAccess(coachData.manualAiAccess === true);
        
        // Load default AI access setting
        setDefaultAiAccess(coachData.defaultAiAccess === true);
        
        // Load affiliation data if it exists
        setAffiliationData({
          link: coachData.affiliationLink || '',
          buttonText: coachData.affiliationButtonText || ''
        });
        
        // Store the document ID for updates
        setCoachDocId(coachSnapshot.docs[0].id);
      }
    } catch (err) {
      console.error("Error loading coach data:", err);
    }
  };

  const checkCoachAccess = async () => {
    try {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (!user) {
          navigate("/login");
          return;
        }

        const isCoach = await isUserCoach(user.uid);
        if (!isCoach) {
          navigate("/");
          return;
        }

        if (auth?.currentUser?.uid) {
          loadDashboardStats();
          loadCoachData();
        }
      });

      return () => unsubscribe();
    } catch (err) {
      console.error("Coach access check failed:", err);
      navigate("/");
    }
  };

  const handleDefaultAiAccessToggle = async () => {
    try {
      setIsUpdatingAiAccess(true);
      
      if (!auth.currentUser?.uid) {
        throw new Error('User not authenticated');
      }

      // Update the coach document with new default AI access setting
      const coachQuery = query(
        collection(db, "coaches"),
        where("userId", "==", auth.currentUser.uid)
      );
      const coachSnapshot = await getDocs(coachQuery);
      
      if (coachSnapshot.empty) {
        throw new Error('Coach document not found');
      }
      
      const coachDocRef = doc(db, 'coaches', coachSnapshot.docs[0].id);
      const newSetting = !defaultAiAccess;
      
      await updateDoc(coachDocRef, {
        defaultAiAccess: newSetting,
        updatedAt: new Date()
      });
      
      setDefaultAiAccess(newSetting);
      showAffiliationToastFunc(
        `Default AI access ${newSetting ? 'enabled' : 'disabled'} for new respondents`, 
        'success'
      );
    } catch (err) {
      console.error('Error updating default AI access:', err);
      showAffiliationToastFunc('Failed to update default AI access setting', 'error');
    } finally {
      setIsUpdatingAiAccess(false);
    }
  };

  const loadDashboardStats = async () => {
    try {
      setIsLoading(true);
      const userId = auth.currentUser?.uid;

      // Get respondents for this coach
      const respondentsQuery = query(
        collection(db, "respondents"),
        where("coachId", "==", userId)
      );
      const respondentsSnapshot = await getDocs(respondentsQuery);
      const totalRespondents = respondentsSnapshot.size;

      // Get completed assessments
      const completedQuery = query(
        collection(db, "results"),
        where("coachId", "==", userId)
      );
      const completedSnapshot = await getDocs(completedQuery);
      const completedCount = completedSnapshot.size;

      // Get active respondents (accessed in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      let activeCount = 0;
      try {
        const activeQuery = query(
          collection(db, "respondents"),
          where("coachId", "==", userId),
          where("lastAssessmentDate", ">=", thirtyDaysAgo)
        );
        const activeSnapshot = await getDocs(activeQuery);
        activeCount = activeSnapshot.size;
      } catch (indexError) {
        console.warn("Active respondents query requires composite index. Skipping for now.", indexError);
        // For now, we'll set active count to 0 until the index is created
        activeCount = 0;
      }

      setStats({
        totalRespondents,
        completedAssessments: completedCount,
        activeRespondents: activeCount,
      });

      // Load recent activity
      if (userId) {
        await loadRecentActivity(userId);
      }
    } catch (err) {
      console.error("Failed to load dashboard stats:", err);
      setError("Failed to load dashboard statistics");
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecentActivity = async (coachId: string) => {
    try {
      const activities: Array<{
        id: string;
        type: 'registration' | 'assessment_completed';
        respondentName: string;
        timestamp: Date;
        details?: string;
      }> = [];

      // Get recent respondent registrations (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Get recent respondent registrations - handle composite index requirement 
      try {
        const respondentsQuery = query(
          collection(db, "respondents"),
          where("coachId", "==", coachId),
          where("createdAt", ">=", thirtyDaysAgo)
        );
        const respondentsSnapshot = await getDocs(respondentsQuery);

        // Process registrations
        for (const doc of respondentsSnapshot.docs) {
          const data = doc.data();

          // Get user name
          const userQuery = query(
            collection(db, "users"),
            where("userId", "==", data.userId)
          );
          const userSnapshot = await getDocs(userQuery);
          const userName = userSnapshot.docs[0]?.data()?.fullName || 'Unknown User';

          activities.push({
            id: doc.id + '_registration',
            type: 'registration',
            respondentName: userName,
            timestamp: data.createdAt.toDate(),
            details: 'New respondent registered'
          });
        }
      } catch (indexError) {
        console.warn("Recent registrations query requires composite index. Skipping for now.", indexError);
      }

      // Get recent completed assessments - handle composite index requirement 
      try {
        const resultsQuery = query(
          collection(db, "results"),
          where("coachId", "==", coachId),
          where("completedAt", ">=", thirtyDaysAgo)
        );
        const resultsSnapshot = await getDocs(resultsQuery);

        // Process completed assessments
        for (const doc of resultsSnapshot.docs) {
          const data = doc.data();

          // Get user name
          const userQuery = query(
            collection(db, "users"),
            where("userId", "==", data.userId)
          );
          const userSnapshot = await getDocs(userQuery);
          const userName = userSnapshot.docs[0]?.data()?.fullName || 'Unknown User';

          activities.push({
            id: doc.id + '_completion',
            type: 'assessment_completed',
            respondentName: userName,
            timestamp: data.completedAt.toDate(),
            details: data.version > 1 ? `Retake #${data.version - 1}` : 'First assessment'
          });
        }
      } catch (indexError) {
        console.warn("Recent assessments query requires composite index. Skipping for now.", indexError);
      }

      // Sort activities by timestamp (most recent first)
      activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      // Take only the latest 10 activities
      setRecentActivity(activities.slice(0, 10));
    } catch (err) {
      console.error("Error loading recent activity:", err);
      // Don't set error state as this is not critical functionality
    }
  };
  if (isLoading) {
    return <LoadingSpinner message="Loading coach dashboard..." />;
  }

  return (
    <div className="min-h-screen minimal-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Brain className="w-12 h-12 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Coach Dashboard</h1>
              <p className="text-gray-600">
                Monitor your assessments and respondents
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 
              hover:bg-gray-200 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Key className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Your Assessment Code
                </h2>
                <p className="text-gray-600">
                  Share this code with your clients
                </p>
              </div>
            </div>
            <button
              onClick={handleCopyCode}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-100 text-blue-600 hover:bg-blue-200 transition-all"
            >
              <Copy className="w-4 h-4" />
              Copy Code
            </button>
          </div>
          <div className="mt-6 p-6 bg-gray-50 rounded-xl">
            <p className="text-4xl font-mono text-gray-900 text-center font-bold">
              {assessmentCode}
            </p>
          </div>
        </div>

        {/* Default AI Access Setting */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Default AI Access for New Respondents
              </h2>
              <p className="text-gray-600">
                When enabled, all new respondents registered with your code will automatically have AI analysis access
              </p>
            </div>
            <button
              onClick={handleDefaultAiAccessToggle}
              disabled={isUpdatingAiAccess}
              className={`px-6 py-3 rounded-xl font-medium transition-all disabled:opacity-50 flex items-center gap-2 ${
                defaultAiAccess
                  ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <div className={`w-4 h-4 rounded-full ${defaultAiAccess ? 'bg-emerald-600' : 'bg-gray-400'}`} />
              {isUpdatingAiAccess 
                ? 'Updating...' 
                : defaultAiAccess 
                  ? 'Default AI Access Enabled' 
                  : 'Default AI Access Disabled'
              }
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-6 bg-blue-50 border border-blue-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {stats.totalRespondents}
                </div>
                <div className="text-sm text-gray-600">Total Respondents</div>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl p-6 bg-emerald-50 border border-emerald-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {stats.completedAssessments}
                </div>
                <div className="text-sm text-gray-600">Completed Assessments</div>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Key className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl p-6 bg-purple-50 border border-purple-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {stats.activeRespondents}
                </div>
                <div className="text-sm text-gray-600">Active Respondents (30d)</div>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate("/coach/respondents")}
              className="p-6 rounded-xl bg-blue-50 border border-blue-100 hover:bg-blue-100 transition-all text-left shadow-sm hover:shadow-md"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900 mb-1">View Respondents</div>
                  <div className="text-sm text-gray-600">
                    Access your respondents' profiles and assessment results
                  </div>
                </div>
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate("/coach/manual-analysis")}
              className="p-6 rounded-xl bg-purple-50 border border-purple-100 hover:bg-purple-100 transition-all text-left shadow-sm hover:shadow-md"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900 mb-1">Manual AI Analysis</div>
                  <div className="text-sm text-gray-600">
                    Enter scores manually to get AI insights
                  </div>
                </div>
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Brain className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Recent Activity
          </h2>
          {recentActivity.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              No recent activity in the last 30 days
            </div>
          ) : (
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-4 h-4 rounded-full ${
                      activity.type === 'registration' 
                        ? 'bg-blue-500' 
                        : 'bg-emerald-500'
                    }`} />
                    <div>
                      <div className="text-gray-900 font-medium">
                        {activity.respondentName}
                      </div>
                      <div className="text-gray-600 text-sm">
                        {activity.type === 'registration' 
                          ? 'Registered as new respondent'
                          : `Completed assessment${activity.details ? ` (${activity.details})` : ''}`
                        }
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {activity.timestamp.toLocaleDateString()} {activity.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Affiliation */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Affiliation
          </h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-gray-900 mb-2">
                Affiliation Link
              </label>
              <input
                type="text"
                value={affiliationData.link}
                onChange={(e) => setAffiliationData(prev => ({
                  ...prev,
                  link: e.target.value
                }))}
                className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
                placeholder="Enter affiliation link"
              />
            </div>
            
            <div>
              <label className="block text-gray-900 mb-2">
                Affiliation Button Text
              </label>
              <input
                type="text"
                value={affiliationData.buttonText}
                onChange={(e) => setAffiliationData(prev => ({
                  ...prev,
                  buttonText: e.target.value
                }))}
                className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
                placeholder="Enter button text"
              />
            </div>
            
            <div className="flex justify-center">
              <button 
                onClick={handleAffiliationUpdate}
                disabled={isUpdating}
                className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 
                  text-white font-medium hover:scale-105 transition-all disabled:opacity-50"
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>

      {/* Toast Notifications */}
      <Toast
        isVisible={showCopyToast}
        message="Assessment code copied to clipboard!"
        type="success"
        onClose={() => setShowCopyToast(false)}
      />
      
      <Toast
        isVisible={showAffiliationToast}
        message={affiliationToastMessage}
        type={affiliationToastType}
        onClose={() => setShowAffiliationToast(false)}
      />
      </div>
    </div>
  );
}